import { useEffect, useRef, useState } from 'react';
import useRealtimeDraft from './useRealtimeDraft';

// A failed blur-save keeps its draft protected. Only that field's latest
// successful write (or leaving the editor) releases it.
export default function useTrackedRealtimeEdits() {
  const edits = useRef(new Map());
  const mounted = useRef(true);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useRealtimeDraft(dirty);
  const mark = key => {
    const token = Symbol(key);
    edits.current.set(key, token);
    setDirty(true);
    return token;
  };
  const track = (id, data, request) => {
    const versions = Object.keys(data).filter(field => field !== 'ID').map(field => {
      const key = `${id}:${field}`;
      return [key, mark(key)];
    });
    request.unwrap().then(() => {
      if (!mounted.current) return;
      versions.forEach(([key, token]) => { if (edits.current.get(key) === token) edits.current.delete(key); });
      setDirty(edits.current.size > 0);
    }).catch(() => { /* Existing save UI handles the failure; retain the draft. */ });
    return request;
  };
  const clear = key => { edits.current.delete(key); setDirty(edits.current.size > 0); };
  return { mark, track, clear };
}
